import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  FaBuilding,
  FaChevronRight,
  FaCloudUploadAlt,
  FaFolder,
  FaLock,
  FaPen,
  FaPlus,
  FaSearch,
  FaShareAlt,
  FaTrash,
  FaUser,
  FaUserTie,
} from "react-icons/fa";
import {
  createLibraryFolder,
  deleteLibraryDocument,
  deleteLibraryFolder,
  downloadLibraryDocument,
  fetchCollaborators,
  fetchDocumentVersions,
  fetchExplorer,
  renameLibraryDocument,
  replaceFolderPermissions,
  updateLibraryFolder,
  uploadDocumentVersion,
  uploadFolderArtwork,
  uploadLibraryDocument,
} from "../../redux/slices/documentLibrarySlice";
import { fetchEmployeeFolders } from "../../redux/slices/organizationDocumentsSlice";
import EmployeeDocumentsPanel from "./EmployeeDocumentsPanel";
import { Dialog, EmptyState, FileTable, FolderGrid, SectionTitle, VersionDialog, scopeIcon } from "./DocumentViews";
import "./DocumentExplorer.css";

const EMPTY_FORM = { name: "", description: "", visibility: "personal", permissions: [] };

// Folders managed in organization settings: listed with their closest place, edited only there.
const ORGANIZATION_PLACES = { private: "company", custom: "restricted" };
const placeOf = (visibility) => ORGANIZATION_PLACES[visibility] || visibility;
const isOrganizationFolder = (folder) => Boolean(ORGANIZATION_PLACES[folder.visibility]);

const FolderDialog = ({ folder, currentFolderId, collaborators, isAdmin, inOrgTree = false, onClose, onSaved }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form, setForm] = useState(folder ? {
    name: folder.name,
    description: folder.description || "",
    visibility: folder.visibility,
    permissions: folder.permissions || [],
  } : EMPTY_FORM);
  const [artwork, setArtwork] = useState(null);
  const [saving, setSaving] = useState(false);

  const updatePermission = (userId, enabled, permission = "view") => {
    setForm((current) => ({
      ...current,
      permissions: enabled
        ? [...current.permissions.filter((item) => item.user_id !== userId), { user_id: userId, permission }]
        : current.permissions.filter((item) => item.user_id !== userId),
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      let savedFolder = folder;
      if (folder) {
        // Inside an organization folder access is inherited, so only the name and description change.
        const updates = inOrgTree
          ? { name: form.name, description: form.description }
          : { name: form.name, description: form.description, visibility: form.visibility };
        savedFolder = await dispatch(updateLibraryFolder({ folderId: folder.id, updates })).unwrap();
        if (!inOrgTree) {
          savedFolder = await dispatch(replaceFolderPermissions({
            folderId: folder.id,
            permissions: form.visibility === "personal" ? [] : form.permissions.map(({ user_id, permission }) => ({ user_id, permission })),
          })).unwrap();
        }
      } else {
        savedFolder = await dispatch(createLibraryFolder(inOrgTree
          ? { name: form.name, description: form.description, visibility: "restricted", parent_id: currentFolderId, permissions: [] }
          : { ...form, parent_id: currentFolderId, permissions: form.visibility === "personal" ? [] : form.permissions },
        )).unwrap();
      }
      if (artwork) {
        await dispatch(uploadFolderArtwork({ folderId: savedFolder.id, image: artwork })).unwrap();
      }
      onSaved();
    } catch (_error) {
      // Rejected thunks store a user-facing error in the document slice.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog title={folder ? t("pages.Documents.editFolder") : t("pages.Documents.newFolder")} onClose={onClose}>
      <form className="document-form" onSubmit={save}>
        <label>
          {t("pages.Documents.folderName")}
          <input required maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label>
          {t("pages.Documents.description")}
          <textarea maxLength={1000} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </label>
        {inOrgTree ? (
          <p className="document-form-note">{t("pages.Documents.accessManagedInSettings")}</p>
        ) : (
          <label>
            {t("pages.Documents.visibility")}
            <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}>
              <option value="personal">{t("pages.Documents.personal")}</option>
              <option value="restricted">{t("pages.Documents.restricted")}</option>
              {isAdmin && <option value="company">{t("pages.Documents.company")}</option>}
            </select>
          </label>
        )}
        {form.visibility !== "personal" && !inOrgTree && (
          <fieldset className="document-access-list">
            <legend>{t("pages.Documents.peopleWithAccess")}</legend>
            {collaborators.map((person) => {
              const current = form.permissions.find((item) => item.user_id === person.id);
              return (
                <div className="document-access-row" key={person.id}>
                  <label>
                    <input type="checkbox" checked={Boolean(current)} onChange={(e) => updatePermission(person.id, e.target.checked)} />
                    <span>{person.name}</span>
                  </label>
                  {current && (
                    <select aria-label={`${person.name} permission`} value={current.permission} onChange={(e) => updatePermission(person.id, true, e.target.value)}>
                      <option value="view">{t("pages.Documents.canView")}</option>
                      <option value="edit">{t("pages.Documents.canEdit")}</option>
                      <option value="manage">{t("pages.Documents.canManage")}</option>
                    </select>
                  )}
                </div>
              );
            })}
          </fieldset>
        )}
        <label>
          {t("pages.Documents.folderImage")}
          <input type="file" accept="image/*" onChange={(e) => setArtwork(e.target.files?.[0] || null)} />
        </label>
        <div className="document-dialog-actions">
          <button type="button" className="document-button document-button--quiet" onClick={onClose}>{t("common.actions.cancel")}</button>
          <button className="document-button document-button--primary" disabled={saving}>{saving ? t("common.actions.loading") : t("common.actions.save")}</button>
        </div>
      </form>
    </Dialog>
  );
};

const DocumentExplorer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fileInput = useRef(null);
  const versionInput = useRef(null);
  const { currentFolder, breadcrumbs, folders, documents, collaborators, versionsByDocument, isLoading, error } = useSelector((state) => state.documents);
  const user = useSelector((state) => state.auth.user);
  const myFolderCount = useSelector((state) => state.organizationDocuments.employeeFolders.length);
  const [rootFolders, setRootFolders] = useState([]);
  const [scope, setScope] = useState("all");
  const [search, setSearch] = useState("");
  const [folderDialog, setFolderDialog] = useState(null);
  const [versionDocument, setVersionDocument] = useState(null);
  const [versionTarget, setVersionTarget] = useState(null);
  const isAdmin = user?.role?.name === "Super Admin";

  useEffect(() => {
    dispatch(fetchExplorer(null));
    dispatch(fetchCollaborators());
    if (user?.id) dispatch(fetchEmployeeFolders(user.id));
  }, [dispatch, user?.id]);

  useEffect(() => {
    if (!currentFolder) setRootFolders(folders);
  }, [currentFolder, folders]);

  const refresh = () => dispatch(fetchExplorer(currentFolder?.id || null));
  const enterFolder = (id) => { setSearch(""); dispatch(fetchExplorer(id)); };
  const goRoot = (nextScope = "all") => { setScope(nextScope); setSearch(""); dispatch(fetchExplorer(null)); };

  const shownFolders = useMemo(() => folders.filter((folder) => {
    const matchesScope = currentFolder || scope === "all" || placeOf(folder.visibility) === scope;
    return matchesScope && folder.name.toLowerCase().includes(search.toLowerCase());
  }), [folders, currentFolder, scope, search]);
  const shownDocuments = useMemo(() => documents.filter((document) =>
    document.title.toLowerCase().includes(search.toLowerCase())), [documents, search]);

  const uploadFiles = async (event) => {
    const selected = [...(event.target.files || [])];
    try {
      for (const file of selected) {
        await dispatch(uploadLibraryDocument({ folderId: currentFolder.id, file })).unwrap();
      }
      refresh();
    } catch (_error) {
      // Rejected thunks store a user-facing error in the document slice.
    }
    event.target.value = "";
  };

  const uploadVersion = async (event) => {
    const file = event.target.files?.[0];
    if (file && versionTarget) {
      try {
        await dispatch(uploadDocumentVersion({ documentId: versionTarget.id, file })).unwrap();
        refresh();
      } catch (_error) {
        // Rejected thunks store a user-facing error in the document slice.
      }
    }
    event.target.value = "";
    setVersionTarget(null);
  };

  const openVersions = async (document) => {
    setVersionDocument(document);
    await dispatch(fetchDocumentVersions(document.id));
  };

  const renameDocument = async (document) => {
    const title = window.prompt(t("pages.Documents.renamePrompt"), document.title);
    if (title?.trim() && title.trim() !== document.title) {
      await dispatch(renameLibraryDocument({ documentId: document.id, title: title.trim() })).unwrap();
      refresh();
    }
  };

  const removeDocument = async (document) => {
    if (window.confirm(t("pages.Documents.deleteFileConfirm", { name: document.title }))) {
      await dispatch(deleteLibraryDocument(document.id)).unwrap();
      refresh();
    }
  };

  const removeFolder = async (folder) => {
    if (window.confirm(t("pages.Documents.deleteFolderConfirm", { name: folder.name }))) {
      await dispatch(deleteLibraryFolder(folder.id)).unwrap();
      refresh();
    }
  };

  const placeCount = (visibility) => rootFolders.filter((folder) => placeOf(folder.visibility) === visibility).length;
  // "My documents" is the signed-in user's own employee file, the same one shown on their profile.
  const showMine = scope === "mine" && Boolean(user?.id);
  const canAdd = !currentFolder || ["edit", "manage"].includes(currentFolder.permission);
  // Inside an organization folder, access is chosen in organization settings, not per folder.
  const inOrgTree = (folder) =>
    Boolean(folder && isOrganizationFolder(folder)) ||
    Boolean(currentFolder && isOrganizationFolder(currentFolder)) ||
    breadcrumbs.some((item) => isOrganizationFolder(item));

  return (
    <main className="document-library">
      <header className="document-library-header">
        <div>
          <h1>{t("pages.Documents.title")}</h1>
          <p>{t("pages.Documents.subtitle")}</p>
        </div>
        <div className="document-header-actions">
          {!showMine && canAdd && <button className="document-button document-button--quiet" onClick={() => setFolderDialog("new")}><FaPlus />{t("pages.Documents.newFolder")}</button>}
          {!showMine && currentFolder && ["edit", "manage"].includes(currentFolder.permission) && (
            <button className="document-button document-button--primary" onClick={() => fileInput.current?.click()}><FaCloudUploadAlt />{t("pages.Documents.uploadFiles")}</button>
          )}
          <input ref={fileInput} hidden multiple type="file" onChange={uploadFiles} />
          <input ref={versionInput} hidden type="file" onChange={uploadVersion} />
        </div>
      </header>

      <div className="document-workspace">
        <aside className="document-places" aria-label={t("pages.Documents.places")}>
          <h2>{t("pages.Documents.places")}</h2>
          <button className={scope === "all" ? "active" : ""} onClick={() => goRoot("all")}><FaFolder /><span>{t("pages.Documents.allFiles")}</span><small>{rootFolders.length}</small></button>
          <button className={scope === "company" ? "active" : ""} onClick={() => goRoot("company")}><FaBuilding /><span>{t("pages.Documents.company")}</span><small>{placeCount("company")}</small></button>
          <button className={scope === "restricted" ? "active" : ""} onClick={() => goRoot("restricted")}><FaShareAlt /><span>{t("pages.Documents.shared")}</span><small>{placeCount("restricted")}</small></button>
          <button className={scope === "personal" ? "active" : ""} onClick={() => goRoot("personal")}><FaUser /><span>{t("pages.Documents.personal")}</span><small>{placeCount("personal")}</small></button>
          {user?.id && (
            <button className={showMine ? "active" : ""} onClick={() => goRoot("mine")}><FaUserTie /><span>{t("pages.Documents.myDocuments")}</span><small>{myFolderCount}</small></button>
          )}
          <div className="document-places-note"><FaLock /><p>{t("pages.Documents.accessNote")}</p></div>
        </aside>

        {showMine ? <EmployeeDocumentsPanel userId={user.id} rootLabel={t("pages.Documents.myDocuments")} /> : (
        <section className="document-browser">
          <div className="document-browser-toolbar">
            <nav className="document-breadcrumbs" aria-label={t("pages.Documents.breadcrumbs")}>
              <button onClick={() => goRoot(scope)}>{t("pages.Documents.documents")}</button>
              {breadcrumbs.map((folder) => <span key={folder.id}><FaChevronRight /><button onClick={() => enterFolder(folder.id)}>{folder.name}</button></span>)}
            </nav>
            <label className="document-search"><FaSearch /><span className="visually-hidden">{t("pages.Documents.search")}</span><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("pages.Documents.searchPlaceholder")} /></label>
          </div>

          {error && <div className="document-error" role="alert">{error}</div>}
          {isLoading ? <div className="document-loading">{t("common.actions.loading")}</div> : (
            <>
              {currentFolder && (
                <div className="document-folder-heading">
                  <div><span>{scopeIcon[currentFolder.visibility]}</span><div><h2>{currentFolder.name}</h2><p>{currentFolder.description || t("pages.Documents.noDescription")}</p></div></div>
                  {currentFolder.permission === "manage" && !isOrganizationFolder(currentFolder) && <button className="document-button document-button--quiet" onClick={() => setFolderDialog(currentFolder)}><FaPen />{t("pages.Documents.manageAccess")}</button>}
                </div>
              )}

              <SectionTitle title={t("pages.Documents.folders")} count={shownFolders.length} />
              {shownFolders.length ? (
                <FolderGrid
                  folders={shownFolders}
                  onOpen={(folder) => enterFolder(folder.id)}
                  renderMeta={(folder) => <><span>{scopeIcon[folder.visibility]}{t(`pages.Documents.${folder.visibility}`)}</span><span>{t(`pages.Documents.permission_${folder.permission}`)}</span></>}
                  renderActions={(folder) => folder.permission === "manage" && !isOrganizationFolder(folder) && (
                    <>
                      <button aria-label={t("pages.Documents.editFolder")} onClick={() => setFolderDialog(folder)}><FaPen /></button>
                      <button className="document-action-danger" aria-label={t("common.actions.delete")} onClick={() => removeFolder(folder)}><FaTrash /></button>
                    </>
                  )}
                />
              ) : <EmptyState icon={<FaFolder />} title={t("pages.Documents.noFolders")} hint={t("pages.Documents.noFoldersHint")} />}

              {currentFolder && (
                <>
                  <SectionTitle className="document-files-heading" title={t("pages.Documents.files")} count={shownDocuments.length} />
                  {shownDocuments.length ? (
                    <FileTable
                      documents={shownDocuments}
                      onDownload={(document) => dispatch(downloadLibraryDocument({ documentId: document.id }))}
                      onVersions={openVersions}
                      onNewVersion={(document) => { setVersionTarget(document); versionInput.current?.click(); }}
                      onRename={renameDocument}
                      onDelete={removeDocument}
                      canChange={(document) => ["edit", "manage"].includes(document.permission)}
                    />
                  ) : (
                    <EmptyState
                      className="document-empty--files"
                      icon={<FaCloudUploadAlt />}
                      title={t("pages.Documents.noFiles")}
                      hint={["edit", "manage"].includes(currentFolder.permission) ? t("pages.Documents.noFilesHint") : t("pages.Documents.noFilesReadOnly")}
                    />
                  )}
                </>
              )}
            </>
          )}
        </section>
        )}
      </div>

      {folderDialog && <FolderDialog folder={folderDialog === "new" ? null : folderDialog} currentFolderId={currentFolder?.id || null} collaborators={collaborators} isAdmin={isAdmin} inOrgTree={inOrgTree(folderDialog === "new" ? null : folderDialog)} onClose={() => setFolderDialog(null)} onSaved={() => { setFolderDialog(null); refresh(); }} />}
      {versionDocument && <VersionDialog document={versionDocument} versions={versionsByDocument[versionDocument.id] || []} onClose={() => setVersionDocument(null)} onDownload={(versionId) => dispatch(downloadLibraryDocument({ documentId: versionDocument.id, versionId }))} />}
    </main>
  );
};

export default DocumentExplorer;
